import { hotkeys } from 'react-keyboard-shortcuts';
import { Typography } from '@material-ui/core';
import PagingFilter from '../../core/PagingFilter';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import ActionText from '../../core/ActionText';
import AreYouSure from '../../core/AreYouSure';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import Locked from '../../core/Locked';
import Notification from '../../core/Notification';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import { globalStyles } from '../../core/style/gstheme';
import TitledPanel from '../../core/TitledPanel';
import { checkResourceEntitlements, getGraphFromProject, makeCancelable, sortObjects, sortResources } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import AddTemplateDialog from './AddTemplateDialog';
import FindClassDialog from './FindClassDialog';
import FindPropertyDialog from './FindPropertyDialog';
import TemplateCard from './TemplateCard';
import TemplateManager from './TemplateManager';
import CommonIriDisplay from '../CommonIriDisplay';
import { BN_NAMESPACE } from '../../core/utils/constants';

const styles = () => {
    return Object.assign({
        associatedProject: {

        }
    }, globalStyles);
};

class AssociatedProject extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            exists: false,
            loading: true,
            templates: [],
            filteredTemplates: [],
            sortedClasses: [],
            globalProps: [],
            advancedDomains: [],
            templateFilterText: "",
            displayTemplateIRI: "",
            isLoaded: (
                (props.data.associatedTemplate && props.data.associatedTemplate.length > 0)
                || (props.data.associatedClass && props.data.associatedClass.length > 0)
                || (props.data.associatedProperty && props.data.associatedProperty.length > 0)
            ) ? false
                :
                true,
            isErrored: false,
            areYouSure: false,
            visibleTemplates: props.data.associatedTemplate ? [...props.data.associatedTemplate] : []
        };
        this.templateRef = React.createRef();
        this.cancellable = null;
    }

    hot_keys = {
        'alt+t': {
            priority: 1,
            handler: () => this.scrollToTemplates(),
        }
    };

    componentDidMount() {
        this.collectData().then(() => {
            this.checkIfExists();
        });
    }

    static getDerivedStateFromProps(props, state) {
        if ((props.data.associatedTemplate || props.data.associatedProperty || props.data.associatedClass) && props.version !== state.version) {
            return {
                isLoaded: false,
                version: props.version
            };
        }
        return null;
    }

    componentDidUpdate() {
        if (!this.state.isLoaded) {
            if (this.props.data.associatedTemplate || this.props.data.associatedClass || this.props.data.associatedProperty) {
                this.collectData();
            }
        }
    }

    async collectData() {
        // We load all the templates
        if (!this.state.isLoaded) {
            const { data } = this.props;
            if (data.associatedTemplate || data.associatedClass || data.associatedProperty) {
                try {
                    let globalData = {
                        sortedGlobals: [],
                        sortedClasses: [],
                        advancedDomains: []
                    };
                    if ((data.associatedClass && data.associatedClass.length > 0)
                        || (data.associatedProperty && data.associatedProperty.length > 0)) {
                        globalData = await this.collectGlobalData();
                        if (this.cancellable && this.cancellable.isCancelled()) return;
                    }
                    let sortedTemplates = [];
                    let filteredTemplates = [];
                    if (data.associatedTemplate && data.associatedTemplate.length > 0) {
                        GS.handle("load-templates");
                        await this.loadData(data.associatedTemplate, this.props.graph).promise;
                        if (this.cancellable && this.cancellable.isCancelled()) return;
                        const templates = this.props.data.associatedTemplate.map(id => GS.data(id, this.props.graph));
                        sortedTemplates = sortObjects(templates, 'label', this.props.defaultLang, null, false)
                            .map(template => template.id);
                        // We need to filter in case incoming 
                        filteredTemplates = [...sortedTemplates].filter(at => {
                            if (!GS.isLoaded(at, this.props.graph)) return false;
                            if (this.state.templateFilterText === "") return true;
                            const text = this.state.templateFilterText.toLowerCase();
                            const atData = GS.data(at, this.props.graph);
                            if (atData.label && atData.label[this.props.defaultLang]
                                && atData.label[this.props.defaultLang].toLowerCase().includes(text)) return true;
                            return false;
                        });
                        let shapesToLoad = [];
                        let pathList = [];
                        // Get all the shapes we need to load
                        data.associatedTemplate.forEach(at => {
                            if (GS.isLoaded(at, this.props.graph)) {
                                const shPropData = GS.data(at, this.props.graph);
                                if (shPropData.shTargetClass && shPropData.shTargetClass.length > 0) {
                                    pathList = pathList.concat(shPropData.shTargetClass);
                                }
                                if (shPropData.shProperty && shPropData.shProperty.length > 0) {
                                    shapesToLoad = shapesToLoad.concat(shPropData.shProperty);
                                }
                            }
                        });
                        if (shapesToLoad.length > 0) {
                            shapesToLoad = [...new Set(shapesToLoad)];
                            await this.loadData(shapesToLoad, this.props.graph).promise;
                            if (this.cancellable && this.cancellable.isCancelled()) return;
                            shapesToLoad.forEach(shProp => {
                                if (GS.isLoaded(shProp, this.props.graph)) {
                                    const shPropData = GS.data(shProp, this.props.graph);
                                    if (shPropData.shPath && !pathList.includes(shPropData.shPath)) {
                                        pathList.push(shPropData.shPath);
                                    }
                                }
                            });
                        }
                        if (pathList.length > 0) {
                            pathList = [...new Set(pathList)];
                            const associatedProjectGraph = getGraphFromProject(data.associatedProject);
                            await this.loadData(pathList, associatedProjectGraph).promise;
                        }
                    }
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    this.setState({
                        isLoaded: true,
                        templates: sortedTemplates,
                        filteredTemplates,
                        globalProps: globalData.sortedGlobals,
                        sortedClasses: globalData.sortedClasses,
                        advancedDomains: globalData.advancedDomains
                    });
                }
                catch (e) {
                    if (!(this.cancellable) || (this.cancellable && !this.cancellable.isCancelled())) {
                        this.setState({ isErrored: e, isLoaded: true });
                    }
                }
            }
            else {
                this.setState({ isLoaded: true });
            }
        }
    }

    async collectGlobalData() {
        try {
            GS.handle("load-globals");
            let dataToLoad = [];
            if (this.props.data.associatedClass && this.props.data.associatedClass.length > 0) {
                dataToLoad = dataToLoad.concat(this.props.data.associatedClass);
            }
            if (this.props.data.associatedProperty && this.props.data.associatedProperty.length > 0) {
                dataToLoad = dataToLoad.concat(this.props.data.associatedProperty);
            }
            if (dataToLoad.length > 0) {
                const associatedProjectGraph = getGraphFromProject(this.props.data.associatedProject);
                await this.loadData(dataToLoad, associatedProjectGraph).promise;
                const sortedClasses = this.props.data.associatedClass ?
                    sortResources(this.props.data.associatedClass, associatedProjectGraph, "label", this.props.defaultLang)
                    :
                    [];
                const sortedGlobals = this.props.data.associatedProperty ?
                    sortResources(this.props.data.associatedProperty, associatedProjectGraph, "label", this.props.defaultLang)
                    :
                    [];
                let advancedDomains = [];
                if (this.props.data.associatedProperty && this.props.data.associatedProperty.length > 0) {
                    this.props.data.associatedProperty.forEach(prop => {
                        const isLoaded = GS.isLoaded(prop, associatedProjectGraph);
                        if (!isLoaded) return;
                        const pData = GS.data(prop, associatedProjectGraph);
                        if (pData.domain && pData.domain.some(d => d.startsWith(BN_NAMESPACE))) {
                            advancedDomains.push(prop);
                        }
                    });
                }
                return { sortedGlobals, sortedClasses, advancedDomains };
            }
            return { sortedGlobals: [], sortedClasses: [], advancedDomains: [] };
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) {
                this.setState({ isErrored: e, isLoaded: true });
            }
            return { sortedGlobals: [], sortedClasses: [], advancedDomains: [] };
        }
    }

    loadData(iris, graph) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, graph));
        return this.cancellable;
    }

    scrollToTemplates = () => {
        if (this.templateRef && this.templateRef.current && !this.props.data.pendingDeletion) {
            this.templateRef.current.scrollIntoView({
                block: 'start',
                inline: 'center',
            });
            this.setState({ displayTemplateIRI: "" });
        }
    };

    onAddTemplate = async (label) => {
        const templateIRI = "https://grafsync.graphifi.com/data/shape/" + uuidv4();
        let newTemplate = {
            id: templateIRI,
            type: "NodeShape",
            label: { [this.props.defaultLang]: label }
        };
        await GS.insertResource(newTemplate, templateIRI, this.props.graph);
        this.props.onAdd("associatedTemplate", templateIRI);
        this.toggleCardView(templateIRI);
    };

    onDeleteTemplate = (iri) => GS.deleteResource(iri, this.props.graph);

    onAddClass = (value) => {
        // Just check somebody else hasn't just added it
        if (!this.props.data.associatedClass || !this.props.data.associatedClass.includes(value)) {
            this.props.onAdd("associatedClass", value);
        }
    };

    onAddProperty = (value) => {
        // Just check somebody else hasn't just added it
        if (!this.props.data.associatedProperty || !this.props.data.associatedProperty.includes(value)) {
            this.props.onAdd("associatedProperty", value);
        }
    };

    doFind(payload) {
        this.cancellable = makeCancelable(GS.find(payload));
        return this.cancellable;
    }

    checkIfExists = async () => {
        try {
            const isFound = await this.doFind({
                value: this.props.data.associatedProject,
                graph: getGraphFromProject(this.props.data.associatedProject)
            }).promise;
            let exists = isFound.exists;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ exists, loading: false });
        }
        catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ loading: false, isErrored: e });
        }
    };

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    checkIfSure = () => this.setState({ areYouSure: true });

    onSure = () => {
        this.setState({ areYouSure: false });
        // Clean up any used templates so they get removed where used
        if (this.props.data.associatedTemplate) {
            for (let at of this.props.data.associatedTemplate) {
                this.onDeleteTemplate(at);
            }
        }
        this.props.onDeleteProject(this.props.id);
    };

    onNotSure = () => this.setState({ areYouSure: false });

    handleTemplateFilterChange = (text) => {
        const filteredTemplates = this.state.templates.filter(at => {
            if (!GS.isLoaded(at, this.props.graph)) return false;
            if (text === "") return true;
            const atData = GS.data(at, this.props.graph);
            if (atData.label && atData.label[this.props.defaultLang]
                && atData.label[this.props.defaultLang].toLowerCase().includes(text.toLowerCase())) return true;
            return false;
        });
        this.setState({ templateFilterText: text.trim(), filteredTemplates });
    };

    toggleCardView = (iri) => {
        let visibleTemplates = [...this.state.visibleTemplates];
        if (visibleTemplates.includes(iri)) {
            visibleTemplates.splice(visibleTemplates.indexOf(iri), 1);
        }
        else {
            visibleTemplates.push(iri);
        }
        this.setState({ visibleTemplates });
    };

    jumpToCard = (iri) => this.setState({ displayTemplateIRI: iri });

    monitorLinkUpdate = async (iri, property) => {
        // This will re-sort if needed
        if (property === "label") {
            const data = await this.collectGlobalData();
            this.setState({
                globalProps: data.sortedGlobals,
                sortedClasses: data.sortedClasses,
                advancedDomains: data.advancedDomains
            });
        }
    };

    render() {

        const { classes, data, defaultLang, graph, lockedGraphs, requiredEntitlement, projectType, modules } = this.props;
        const { exists, loading, isErrored, areYouSure, visibleTemplates, templates, displayTemplateIRI,
            templateFilterText, filteredTemplates, globalProps, sortedClasses, advancedDomains } = this.state;

        if (isErrored) return <Errored dataTest="ap-error" data={isErrored} />;
        const isReadOnly = data.pendingDeletion || !exists || checkResourceEntitlements(data, requiredEntitlement);
        const associatedProjectGraph = getGraphFromProject(data.associatedProject);
        const locked = lockedGraphs.some(lg => getGraphFromProject(data.associatedProject) === lg);
        if (locked) return <Locked />;
        if (data.pendingDeletion) return <Notification error={true} spaceRight={true} dataTest="pending-deletion" text="This project association is pending deletion." />;
        const containsAdvancedDomains = advancedDomains.length > 0;

        return (
            <ProjectConsumer>
                {({ id: projectIRI, skosXL }) => (
                    <div data-test={data.associatedProject} elevation={0} className={classes.associatedProject}>
                        {loading ?
                            <Loading />
                            :
                            <div>
                                <div style={{ display: 'flex', marginBottom: 12 }}>
                                    <div style={{ flex: '1' }}>
                                        {!exists &&
                                            <Notification error={false} text="The associated project has been deleted." dataTest="warning" />
                                        }
                                    </div>
                                    {!isReadOnly &&
                                        <div style={{ flex: '0 0' }} data-test="delete-associated-project-option" >
                                            <Tooltip placement="right" title='Delete project from template manager'>
                                                <IconButton
                                                    data-test="delete-associated-project"
                                                    aria-label="Delete project from template manager"
                                                    size="small"
                                                    onClick={this.checkIfSure}
                                                    className={classes.deleteButton}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    }
                                </div>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={6}>
                                        <TitledPanel dataTest="associated-classes" height="full" title="Classes to Make Available" actions=
                                            {!isReadOnly ?
                                                <FindClassDialog
                                                    defaultLang={defaultLang}
                                                    projects={[data.associatedProject]}
                                                    exclude={data.associatedClass}
                                                    onAddClass={this.onAddClass} />
                                                :
                                                null
                                            }>
                                            {data.associatedClass &&
                                                sortedClasses.map((ac, index) =>
                                                    <CommonIriDisplay
                                                        dataTest={"class-resource-link-" + index}
                                                        key={ac}
                                                        id={ac}
                                                        projectIRI={data.associatedProject}
                                                        loadComponents={true}
                                                        checkIfUsedOnDelete={
                                                            {
                                                                graph,
                                                                value: ac,
                                                                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                                                                message: [
                                                                    "This associated class is used within this project.",
                                                                    "Removing it will not remove data using it but will alter the class link to be displayed as an IRI.",
                                                                    "Do you want to proceed with removing this class?"
                                                                ]
                                                            }
                                                        }
                                                        highlightNotAvailable={true}
                                                        handleNotAvailable={true}
                                                        updateEvent={this.monitorLinkUpdate}
                                                        isReadOnly={isReadOnly}
                                                        sourceGraph={graph}
                                                        wrap={false}
                                                        graph={associatedProjectGraph}
                                                        property="associatedClass"
                                                        source={data.id}
                                                        viewLang={defaultLang}
                                                        defaultLang={defaultLang} />
                                                )}
                                        </TitledPanel>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TitledPanel dataTest="associated-properties" height="full" title="Global Properties" actions=
                                            {!isReadOnly && exists ?
                                                <FindPropertyDialog
                                                    defaultLang={defaultLang}
                                                    projects={[data.associatedProject]}
                                                    exclude={data.associatedProperty}
                                                    onAddProperty={this.onAddProperty} />
                                                :
                                                null
                                            }>
                                            {containsAdvancedDomains &&
                                                <div style={{ marginTop: 12, marginBottom: 6 }} data-test="advanced-domains-warning">
                                                    <Notification
                                                        error={false}
                                                        text="Some properties have complex domain constructs. These constructs will be ignored."
                                                        dataTest="domain-warning" />
                                                </div>
                                            }
                                            {data.associatedProperty && data.associatedProperty.length > 0 &&
                                                globalProps.map((ap, index) =>
                                                    <CommonIriDisplay
                                                        dataTest={"property-resource-link-" + index}
                                                        key={ap}
                                                        id={ap}
                                                        projectIRI={data.associatedProject}
                                                        loadComponents={true}
                                                        checkIfUsedOnDelete={
                                                            {
                                                                graph,
                                                                predicate: GS.getIRIfromAlias(ap) || ap,
                                                                message: [
                                                                    "This associated property is used within this project.",
                                                                    "Removing it will not remove data using it but the data may only be visible in the Triples Viewer.",
                                                                    "Do you want to proceed with removing this property?"
                                                                ]
                                                            }
                                                        }
                                                        highlightNotAvailable={true}
                                                        handleNotAvailable={true}
                                                        isReadOnly={isReadOnly}
                                                        updateEvent={this.monitorLinkUpdate}
                                                        sourceGraph={graph}
                                                        wrap={false}
                                                        graph={associatedProjectGraph}
                                                        property="associatedProperty"
                                                        source={data.id}
                                                        viewLang={defaultLang}
                                                        defaultLang={defaultLang} />
                                                )}
                                        </TitledPanel>
                                    </Grid>
                                    <Grid item xs={12} ref={this.templateRef}>
                                        <TitledPanel dataTest="templates" height="full" title="Templates" actions=
                                            {!isReadOnly ?
                                                <AddTemplateDialog defaultLang={defaultLang} onAddTemplate={this.onAddTemplate} />
                                                :
                                                null
                                            }>
                                            <div style={{ width: '100%' }}>
                                                {templates && templates.length > 1 &&
                                                    <div>
                                                        <div style={{ maxWidth: '240px', marginBottom: 12 }}>
                                                            <Typography data-test="showing" variant="body2" className={classes.showing}>Showing {filteredTemplates.length} of {templates.length}.</Typography>
                                                            <PagingFilter
                                                                includeLabel={false}
                                                                handleFilterChange={this.handleTemplateFilterChange}
                                                                placeholder="Filter templates"
                                                                text={templateFilterText}
                                                                maxLength={50} />
                                                        </div>
                                                        {displayTemplateIRI !== "" &&
                                                            <div style={{ position: 'fixed', right: 10, bottom: 10, backgroundColor: '#13294B', border: 'solid 4px #13294B', borderRadius: 4 }}>
                                                                <Tooltip placement="right" title='Jump to template filter (Alt+t)'>
                                                                    <IconButton
                                                                        data-test="jump-to-filter"
                                                                        size="small"
                                                                        style={{ color: 'white' }}
                                                                        onClick={this.scrollToTemplates}>
                                                                        <ArrowUpwardIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </div>
                                                        }
                                                    </div>
                                                }
                                                {templates && templates.length > 0 ?
                                                    <div style={{ width: '100%' }}>
                                                        <div data-test="card-list" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', width: '100%' }}>
                                                            {templates
                                                                .filter(at => filteredTemplates.includes(at))
                                                                .map((at) =>
                                                                    <TemplateCard
                                                                        key={at}
                                                                        graph={graph}
                                                                        jumpToCard={this.jumpToCard}
                                                                        onToggleView={this.toggleCardView}
                                                                        visible={visibleTemplates.includes(at)}
                                                                        defaultLang={defaultLang}
                                                                        id={at} />
                                                                )}
                                                        </div>
                                                        <div data-test="template-list" style={{ marginTop: 24 }}>
                                                            {visibleTemplates.length === 0 ?
                                                                <ActionText offset="30px">Select templates to view.</ActionText>
                                                                :
                                                                templates
                                                                    .filter(at => visibleTemplates.includes(at) && filteredTemplates.includes(at))
                                                                    .map((at, index) =>
                                                                        <div
                                                                            data-test={"template-" + index}
                                                                            key={at}
                                                                            style={{
                                                                                backgroundColor: 'white',
                                                                                padding: 12,
                                                                                marginBottom: 36,
                                                                                borderRadius: 4
                                                                            }}>
                                                                            <TemplateManager
                                                                                key={at}
                                                                                dataset={projectType === "DataProject"}
                                                                                skosXL={skosXL}
                                                                                conceptTarget={projectType === "TaxonomyProject"}
                                                                                skos={projectType === "TaxonomyProject"}
                                                                                enableProjectSelection={projectType === "DataProject" || projectType === "TaxonomyProject"}
                                                                                includeSelectFrom={projectType !== "OntologyProject"}
                                                                                graph={graph}
                                                                                modules={modules}
                                                                                displayTemplateIRI={displayTemplateIRI}
                                                                                project={projectIRI}
                                                                                isReadOnly={isReadOnly}
                                                                                onDeleteTemplate={this.onDeleteTemplate}
                                                                                allowedClasses={data.associatedClass}
                                                                                globalProps={data.associatedProperty}
                                                                                ontologyProject={data.associatedProject}
                                                                                ontologyProjectGraph={associatedProjectGraph}
                                                                                defaultLang={defaultLang}
                                                                                id={at} />
                                                                        </div>
                                                                    )
                                                            }
                                                        </div>
                                                    </div>
                                                    :
                                                    <ActionText offset="30px" bottom="50px">Add templates to create sets of properties.</ActionText>
                                                }
                                            </div>
                                        </TitledPanel>
                                    </Grid>
                                </Grid>
                            </div>
                        }
                        {areYouSure && <AreYouSure
                            onSure={this.onSure}
                            onNotSure={this.onNotSure}
                            message={[
                                "Do you really want to delete this association?",
                                "If there are classes that have been used the data will not be removed but the classes will revert to being displayed as IRIs.",
                                "If there are properties that have been used the data will not be removed but will only be visible in the Triple Viewer."
                            ]} />}
                    </div>
                )
                }
            </ProjectConsumer>
        );
    }
}

AssociatedProject.propTypes = {
    classes: PropTypes.object,
    id: PropTypes.string.isRequired,
    modules: PropTypes.number,
    graph: PropTypes.string.isRequired,
    requiredEntitlement: PropTypes.string.isRequired,
    projectType: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    onDeleteProject: PropTypes.func.isRequired,
    onAdd: PropTypes.func,
    lockedGraphs: PropTypes.array,
    data: PropTypes.object.isRequired
};

export default withStyles(styles)(withGS(hotkeys(AssociatedProject)));